import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Building2, Mail, Lock, User, Loader2 } from 'lucide-react';
import { EnterpriseNumberField } from '../components/auth/EnterpriseNumberField';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    user_type: 'both'
  });
  const [enterpriseNumber, setEnterpriseNumber] = useState((searchParams.get('bce') || '').replace(/\D/g, ''));
  const [kbo, setKbo] = useState(null);
  const [foreign, setForeign] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVerified = (data) => {
    setKbo(data);
    if (data) setFormData((prev) => ({ ...prev, company_name: data.name || prev.company_name }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!foreign && !kbo) {
      setError("Veuillez vérifier votre numéro d'entreprise belge (BCE) avant de continuer, ou cochez « Mon entreprise n'est pas belge ».");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...registerData } = formData;
      if (kbo && !foreign) {
        registerData.vat_number = `BE${kbo.enterprise_number}`;
        registerData.address = kbo.street;
        registerData.city = kbo.city;
      }
      await register(registerData);
      toast.success('Compte créé avec succès!');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      toast.error("Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12 bg-[#F9FAFB]">
      <SEOHead title="Inscription gratuite – donneurs d'ordre et sous-traitants" description="Créez votre compte BuildConnect en 2 minutes : vérification automatique BCE, 3 publications de chantier par mois offertes, candidatures illimitées. Belgique, Luxembourg, France." path="/register" noindex={false} />
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#1E3A8A] rounded-full mx-auto mb-4 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="font-heading font-bold text-2xl">{t('auth.registerTitle')}</CardTitle>
          <CardDescription>Créez votre compte BuildConnect</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <EnterpriseNumberField
              value={enterpriseNumber} onChange={setEnterpriseNumber}
              verified={kbo} onVerified={handleVerified}
              foreign={foreign} onForeignChange={setForeign}
            />

            <div className="space-y-2">
              <Label htmlFor="company_name">{t('auth.companyName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
                <Input
                  id="company_name" name="company_name" type="text"
                  placeholder="Nom de votre entreprise"
                  value={formData.company_name} onChange={handleChange}
                  className="pl-10 input-field" required data-testid="register-company-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
                <Input
                  id="email" name="email" type="email"
                  placeholder="votre@email.com"
                  value={formData.email} onChange={handleChange}
                  className="pl-10 input-field" required data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
                <Input
                  id="password" name="password" type="password"
                  placeholder="Min. 6 caractères"
                  value={formData.password} onChange={handleChange}
                  className="pl-10 input-field" required data-testid="register-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
                <Input
                  id="confirmPassword" name="confirmPassword" type="password"
                  placeholder="Confirmez le mot de passe"
                  value={formData.confirmPassword} onChange={handleChange}
                  className="pl-10 input-field" required data-testid="register-confirm-password"
                />
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.loading')}</>) : t('auth.registerTitle')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1E3A8A]/10"></div></div>
            <div className="relative flex justify-center text-sm"><span className="bg-white px-4 text-[#4B5563]">{t('auth.orContinueWith')}</span></div>
          </div>

          <Button variant="outline" className="w-full" onClick={handleGoogleLogin} data-testid="google-register">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.googleLogin')}
          </Button>

          <p className="text-center text-sm text-[#4B5563] mt-6">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-[#1E3A8A] hover:underline font-medium" data-testid="login-link">
              {t('nav.login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
